// Provider Referral Workflow Types
// Following BiteWise naming conventions: camelCase for TypeScript, snake_case for API

import type { LanguageCode } from './languageCodes';

// Health Conditions
export type HealthCondition =
    "Addiction"
  | "Aged care"
  | "Anaemia"
  | "Adolescent nutrition"
  | "Arthritis"
  | "Aboriginal and Torres Strait Islander nutrition care"
  | "Burns"
  | "Bariatric surgery"
  | "Cancer"
  | "Childhood nutrition"
  | "Coeliac disease"
  | "Cystic fibrosis"
  | "Diabetes - Type 1"
  | "Diabetes - Type 2"
  | "Diabetes during pregnancy"
  | "Disability"
  | "Dental health"
  | "Eating disorders"
  | "Fertility nutrition"
  | "Food allergy/sensitivity"
  | "Gastrointestinal (stomach/bowel) disorders"
  | "Genetic disorders"
  | "Healthy eating advice"
  | "Heart disease (High cholesterol, blood pressure)"
  | "Human Immunodeficiency Virus (HIV)"
  | "Inflammatory Bowel Disease (IBD)"
  | "Irritable Bowel Syndrome (IBS)"
  | "Infant feeding"
  | "Integrative medicine"
  | "Kidney disease"
  | "Lactation nutrition"
  | "LGBTQI+ health"
  | "Liver and pancreas disorders"
  | "Lung (respiratory) disease"
  | "Malnutrition"
  | "Men's health"
  | "Metabolic disorders (and syndrome)"
  | "Mental health"
  | "Osteoporosis and low bone density (Osteopenia)"
  | "Overweight and obesity"
  | "Palliative care"
  | "PCOS"
  | "Pre-diabetes"
  | "Pregnancy nutrition"
  | "Rehabilitation"
  | "Sports nutrition"
  | "Swallowing difficulties"
  | "Weight management"
  | "Women's health";

// Local file data interface for storing file information before submission
export interface LocalFileData {
  fileData: string; // Base64 encoded file data
  filename: string;
  mimeType: string;
  description?: string;
  size: number;
}

// Patient Information
export interface PatientData {
  patientName: string;
  dateOfBirth: string;
  medicareNumber: string;
  address: string;
  phone: string;
  email: string;
  conditions: HealthCondition[];
  additionalNotes?: string;
  documents?: LocalFileData[]; // Local file storage for later submission
}

// Dietitian Profile
export type AvailabilityStatus = 'accepting' | 'limited' | 'unavailable';

export interface DietitianProfile {
  id: string;
  name: string;
  credentials: string;
  specialties: HealthCondition[];
  languagesSpoken: LanguageCode[];
  location: string;
  rating: number;
  reviewCount: number;
  availability: AvailabilityStatus;
  nextAvailable: string;
  bio: string;
  avatar?: string;
}

// Referral Submission
export interface ReferralSubmission {
  patientData: PatientData;
  dietitianId: string;
  providerId: string;
  submissionDate: string;
  notes?: string;
}

export interface ReferralSubmissionResult {
  referralId: string;
  status: 'success' | 'error';
  message: string;
  submissionDate: string;
  patientName?: string;
  dietitianName?: string;
  expiresAt?: string;
  fieldErrors?: Record<string, string>;
}

// API Request Types for Referral Submission
export interface ReferralSubmissionRequest {
  patient_name: string;
  date_of_birth: string;
  medicare_number: string;
  address: string;
  phone: string;
  email: string;
  conditions: string[];
  additional_notes?: string;
  dietitian_id: string;
  service_type?: string;
  sessions_approved?: number;
  priority?: string;
}

// API Response Types for Referral Submission
export interface ReferralSubmissionResponse {
  id: string;
  referral_token: string;
  /** provider_form | network_link | direct_booking (design-care-coordinator-referral-tracking §4.2); informational only */
  referral_channel?: string;
  referring_physician_id: string;
  dietitian_id: string;
  patient_name: string;
  date_of_birth: string;
  patient_id_number: string;
  patient_id_type: string;
  phone: string;
  email: string;
  conditions: string[];
  additional_notes: string;
  status: string;
  claimed_by_user_id: string | null;
  claimed_at: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string | null;
  referring_physician_name: string;
  referring_physician_email: string;
  referring_physician_role?: string;
  dietitian_name: string;
  dietitian_email: string;
  dietitian_scheduling_uri: string;
}

// API Error Types
export interface ReferralApiErrorData {
  message: string;
  code?: string;
  fieldErrors?: Record<string, string>;
}

// Filter State
export interface FilterState {
  search: string;
  specialty: string;
  location: string;
}

// Validation Errors
export interface ValidationErrors {
  patientName?: string;
  dateOfBirth?: string;
  medicareNumber?: string;
  address?: string;
  phone?: string;
  email?: string;
  conditions?: string;
  dietitianSelection?: string;
}

// Workflow State
export interface ReferralWorkflowState {
  currentStep: 1 | 2 | 3;
  patientData: Partial<PatientData>;
  selectedDietitian: DietitianProfile | null;
  availableDietitians: DietitianProfile[];
  filters: FilterState;
  isLoading: boolean;
  errors: ValidationErrors;
  isSubmitting: boolean;
  submissionResult: ReferralSubmissionResult | null;
}

// API Request Types (snake_case for backend compatibility)
export interface CreateReferralRequest {
  patient_name: string;
  date_of_birth: string;
  medicare_number: string;
  address: string;
  phone: string;
  email: string;
  health_conditions: string[];
  additional_notes?: string;
  dietitian_id: string;
  provider_id: string;
}

export interface GetDietitiansRequest {
  specialty?: string;
  location?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

// API Response Types (snake_case from backend)
export interface CreateReferralResponse {
  referral_id: string;
  status: string;
  message: string;
  submission_date: string;
}

export interface GetDietitiansResponse {
  dieticians: DietitianApiProfile[];
  total_count: number;
  has_more: boolean;
}

export interface DietitianApiProfile {
  id: string;
  name: string;
  credentials: string;
  specialties: string[];
  location: string;
  rating: number;
  review_count: number;
  availability_status: string;
  next_available: string;
  bio: string;
  avatar_url?: string;
}

// New API profile type matching the actual API documentation
export interface DietitianApiResponseProfile {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  apd_id: string;
  areas_of_practice: string[];
  languages_spoken: LanguageCode[];
  phone: string;
  practice_street_address?: string | null;
  practice_city?: string | null;
  practice_state?: string | null;
  practice_postal_code?: string | null;
  practice_country?: string | null;
  photo?: string | null;
  scheduling_uri: string;
  created_at: string;
  updated_at: string;
}

// Hook Return Types
export interface UseReferralWorkflowReturn {
  state: ReferralWorkflowState;
  actions: {
    setCurrentStep: (step: number) => void;
    updatePatientData: (data: Partial<PatientData>) => void;
    selectDietitian: (dietitian: DietitianProfile) => void;
    updateFilters: (filters: Partial<FilterState>) => void;
    submitReferral: () => Promise<void>;
    resetWorkflow: () => void;
  };
  validation: {
    validateStep: (step: number) => boolean;
    getStepErrors: (step: number) => string[];
  };
}

export interface UseReferralPersistenceReturn {
  saveToStorage: (data: Partial<ReferralWorkflowState>) => void;
  loadFromStorage: () => Partial<ReferralWorkflowState> | null;
  clearStorage: () => void;
  isStorageAvailable: boolean;
}

export interface UseDietitianFilteringReturn {
  filteredDietitians: DietitianProfile[];
  resultCount: number;
  applyFilters: (filters: FilterState) => void;
  clearFilters: () => void;
  isFiltered: boolean;
}

// Constants
export const HEALTH_CONDITIONS: HealthCondition[] = [
  "Addiction",
  "Aged care",
  "Anaemia",
  "Adolescent nutrition",
  "Arthritis",
  "Aboriginal and Torres Strait Islander nutrition care",
  "Burns",
  "Bariatric surgery",
  "Cancer",
  "Childhood nutrition",
  "Coeliac disease",
  "Cystic fibrosis",
  "Diabetes - Type 1",
  "Diabetes - Type 2",
  "Diabetes during pregnancy",
  "Disability",
  "Dental health",
  "Eating disorders",
  "Fertility nutrition",
  "Food allergy/sensitivity",
  "Gastrointestinal (stomach/bowel) disorders",
  "Genetic disorders",
  "Healthy eating advice",
  "Heart disease (High cholesterol, blood pressure)",
  "Human Immunodeficiency Virus (HIV)",
  "Inflammatory Bowel Disease (IBD)",
  "Irritable Bowel Syndrome (IBS)",
  "Infant feeding",
  "Integrative medicine",
  "Kidney disease",
  "Lactation nutrition",
  "LGBTQI+ health",
  "Liver and pancreas disorders",
  "Lung (respiratory) disease",
  "Malnutrition",
  "Men's health",
  "Metabolic disorders (and syndrome)",
  "Mental health",
  "Osteoporosis and low bone density (Osteopenia)",
  "Overweight and obesity",
  "Palliative care",
  "PCOS",
  "Pre-diabetes",
  "Pregnancy nutrition",
  "Rehabilitation",
  "Sports nutrition",
  "Swallowing difficulties",
  "Weight management",
  "Women's health",
];

// Referral Claim Types
export interface ReferralClaimRequest {
  referral_token: string;
}

export interface ReferralClaimResponse {
  id: string;
  referral_token: string;
  /** provider_form | network_link | direct_booking (design-care-coordinator-referral-tracking §4.2); informational only */
  referral_channel?: string;
  referring_physician_id: string;
  dietitian_id: string;
  patient_name: string;
  date_of_birth: string;
  patient_id_number: string;
  patient_id_type: string;
  phone: string;
  email: string;
  conditions: string[];
  additional_notes: string;
  status: string;
  claimed_by_user_id: string;
  claimed_at: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  referring_physician_name: string;
  referring_physician_email: string;
  referring_physician_role?: string;
  dietitian_name: string;
  dietitian_email: string;
  dietitian_scheduling_uri: string;
}

export interface ReferralClaimError {
  message: string;
  code: 'INVALID_TOKEN' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'ALREADY_CLAIMED' | 'EXPIRED' | 'NETWORK_ERROR';
  details?: Record<string, unknown>;
}

// Referral Claim Hook Types
export interface UseReferralClaimReturn {
  claimReferral: (token?: string) => Promise<ReferralClaimResponse | null>;
  isLoading: boolean;
  error: ReferralClaimError | null;
  clearError: () => void;
}
